// redux/slices/propertySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// 🔥 1. GET ALL (with filters)
export const fetchProperties = createAsyncThunk(
  "property/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/property/all", { params });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 2. GET SINGLE
export const fetchPropertyById = createAsyncThunk(
  "property/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/property/single/${id}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 3. CREATE
export const createProperty = createAsyncThunk(
  "property/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post("/property/create", data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 4. UPDATE
export const updateProperty = createAsyncThunk(
  "property/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/property/update/${id}`, data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 5. DELETE
export const deleteProperty = createAsyncThunk(
  "property/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/property/delete/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 6. MY PROPERTIES
export const fetchMyProperties = createAsyncThunk(
  "property/my",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/property/my");
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// 🔥 7. CONTACT (MONETIZATION)
export const fetchContact = createAsyncThunk(
  "property/contact",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/property/contact/${id}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

const propertySlice = createSlice({
  name: "property",
  initialState: {
    properties: [],
    myProperties: [],
    selectedProperty: null,
    contact: null,

    pagination: {
      total: 0,
      page: 1,
      pages: 1
    },

    loading: false,
    error: null
  },

  reducers: {
    clearSelectedProperty: (state) => {
      state.selectedProperty = null;
    }
  },

  extraReducers: (builder) => {
    builder

      // ✅ FETCH ALL
      .addCase(fetchProperties.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.loading = false;

        state.properties = action.payload.properties;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages
        };
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ SINGLE
      .addCase(fetchPropertyById.fulfilled, (state, action) => {
        state.selectedProperty = action.payload;
      })

      // ✅ CREATE
      .addCase(createProperty.fulfilled, (state, action) => {
        state.properties.unshift(action.payload);
      })

      // ✅ UPDATE
      .addCase(updateProperty.fulfilled, (state, action) => {
        const index = state.properties.findIndex(
          (p) => p._id === action.payload._id
        );
        if (index !== -1) {
          state.properties[index] = action.payload;
        }
      })

      // ✅ DELETE
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.properties = state.properties.filter(
          (p) => p._id !== action.payload
        );
      })

      // ✅ MY PROPERTIES
      .addCase(fetchMyProperties.fulfilled, (state, action) => {
        state.myProperties = action.payload;
      })

      // ✅ CONTACT
      .addCase(fetchContact.fulfilled, (state, action) => {
        state.contact = action.payload;
      });
  }
});

export const { clearSelectedProperty } = propertySlice.actions;

export default propertySlice.reducer;